'use client';

import { useMatter } from '@/lib/services/matters/hooks';
import { getPmsProviderLabel } from '@/lib/services/integrations/pms/provider-presentation';
import { canImportDocumentsFromPms } from '@/lib/services/matters/pms-sync';

/**
 * The provider label for {@link AddDocument}'s `pms` source — the matter's PMS provider as the
 * registry names it, or `undefined` when the matter is not PMS-connected **or is synced from a
 * provider whose inbound document import does not work** (LF-6309 — see
 * {@link canImportDocumentsFromPms}).
 *
 * This is the single definition of "is this matter PMS-connected, and may we offer its documents"
 * (LF-6261). What the provider is *called* is owned one level down, by
 * {@link getPmsProviderLabel} — this hook title-cased the slug itself until LF-6344, and the header
 * and scribe tab had each grown their own copy of the same fragile derivation rather than adopting
 * this hook.
 *
 * `AddDocument` calls this itself whenever `sources` includes `pms` and no
 * explicit `pmsProviderLabel` prop was passed, so **a surface only has to list
 * `'pms'` in `sources`** — the gate comes for free. Call it directly only when
 * the label is needed for the caller's own copy or gating (the redemption sheet
 * builds its `sources` array from it).
 *
 * The backend independently enforces PMS-enabled + matter-synced on the import
 * endpoint, so this is a UI gate, not the security boundary.
 *
 * @param matterId the matter to read
 * @param enabled  skip the matter fetch entirely when the caller can't offer PMS
 */
export function usePmsProviderLabel(
  matterId: number,
  enabled = true
): string | undefined {
  const { data: matter } = useMatter(matterId, {
    enabled: enabled && !!matterId
  });
  const source = matter?.metadata?.pms?.source;
  if (!source || !canImportDocumentsFromPms(matter)) return undefined;
  return getPmsProviderLabel(source);
}
